import React, { useState, useEffect, useCallback, useContext } from 'react';
import { votesApi, filesApi } from '../../api';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSlider from '../controls/HrnbSlider';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbFileUpload from '../controls/HrnbFileUpload';
import '../../css/components/BattleVoting.css';
import {UserContext} from "../../context/UserContext";

const SESSION_STATUS = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
};

const ROUND_STATUS = {
    PENDING: 'Ожидание',
    RUNNING: 'Идёт',
    FINISHED: 'Завершён',
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const BattleVoting = ({ battleId, rounds = [] }) => {
    const { currentUser } = useContext(UserContext);
    const [sessions, setSessions] = useState({});
    const [results, setResults] = useState({});
    const [userVotes, setUserVotes] = useState({});
    const [votingRules, setVotingRules] = useState({});
    const [loading, setLoading] = useState({});
    const [voteForms, setVoteForms] = useState({});
    const [voiceCommentFiles, setVoiceCommentFiles] = useState({});
    const [uploadingVoice, setUploadingVoice] = useState({});
    const [error, setError] = useState(null);

    // Загрузка правила голосования для раунда
    const fetchVotingRule = useCallback(async (roundId) => {
        try {
            const data = await votesApi.getRule(battleId, roundId);
            setVotingRules(prev => ({ ...prev, [roundId]: data }));
        } catch (err) {
            console.error(`[BattleVoting] Failed to fetch voting rule for round ${roundId}:`, err.message);
            setVotingRules(prev => ({ ...prev, [roundId]: null }));
        }
    }, [battleId]);

    // Загрузка сессии голосования для раунда
    const fetchSession = useCallback(async (roundId) => {
        try {
            const data = await votesApi.getSession(battleId, roundId);
            setSessions(prev => ({ ...prev, [roundId]: data }));
        } catch (err) {
            console.error(`[BattleVoting] Failed to fetch session for round ${roundId}:`, err.message);
            setSessions(prev => ({ ...prev, [roundId]: null }));
        }
    }, [battleId]);

    // Загрузка результатов голосования
    const fetchResults = useCallback(async (roundId) => {
        try {
            const data = await votesApi.getResults(battleId, roundId);
            setResults(prev => ({ ...prev, [roundId]: data }));
        } catch (err) {
            console.error(`[BattleVoting] Failed to fetch results for round ${roundId}:`, err.message);
        }
    }, [battleId]);

    // Проверка, голосовал ли пользователь
    const checkUserVoted = useCallback(async (roundId, trackId) => {
        if (!currentUser || !trackId) return;
        try {
            const data = await votesApi.hasVoted(battleId, roundId, trackId);
            setUserVotes(prev => ({ ...prev, [`${roundId}-${trackId}`]: data.hasVoted }));
        } catch (err) {
            console.error(`[BattleVoting] Failed to check vote for round ${roundId}:`, err.message);
        }
    }, [battleId, currentUser]);

    // Загрузка данных для всех раундов
    useEffect(() => {
        rounds.forEach(round => {
            fetchVotingRule(round.id);
            fetchSession(round.id);
            fetchResults(round.id);
            if (round.trackId) {
                checkUserVoted(round.id, round.trackId);
            }
        });
    }, [rounds, fetchVotingRule, fetchSession, fetchResults, checkUserVoted]);

    const handleVoteChange = (roundId, field, value) => {
        setVoteForms(prev => ({
            ...prev,
            [roundId]: { ...prev[roundId], [field]: value }
        }));
    };

    const handleVoiceCommentUpload = (roundId, file) => {
        setVoiceCommentFiles(prev => ({ ...prev, [roundId]: file }));
    };

    const handleCriteriaScoreChange = (roundId, criterionName, score) => {
        setVoteForms(prev => {
            const roundForm = prev[roundId] || {};
            const criteriaScores = roundForm.criteriaScores || [];
            const existingIndex = criteriaScores.findIndex(cs => cs.criterionName === criterionName);

            let newCriteriaScores;
            if (existingIndex >= 0) {
                newCriteriaScores = criteriaScores.map((cs, idx) =>
                    idx === existingIndex ? { ...cs, score } : cs
                );
            } else {
                newCriteriaScores = [...criteriaScores, { criterionName, score }];
            }

            // Вычисляем общую сумму
            const rule = votingRules[roundId];
            const totalScore = newCriteriaScores.reduce((sum, cs) => sum + (cs.score || 0), 0);

            return {
                ...prev,
                [roundId]: {
                    ...roundForm,
                    criteriaScores: newCriteriaScores,
                    score: totalScore
                }
            };
        });
    };

    const handleSubmitVote = async (roundId, trackId) => {
        const form = voteForms[roundId] || {};
        const rule = votingRules[roundId];
        const voiceFile = voiceCommentFiles[roundId];

        let voiceCommentId = form.voiceCommentId;

        // Если загружен новый файл, загружаем его сначала
        if (voiceFile) {
            setUploadingVoice(prev => ({ ...prev, [roundId]: true }));
            try {
                const uploadResult = await votesApi.uploadVoiceComment(voiceFile);
                voiceCommentId = uploadResult.id;
            } catch (err) {
                setError(err.message || 'Не удалось загрузить голосовой комментарий');
                console.error('[BattleVoting] Failed to upload voice comment:', err);
                setUploadingVoice(prev => ({ ...prev, [roundId]: false }));
                return;
            }
            setUploadingVoice(prev => ({ ...prev, [roundId]: false }));
        }

        const payload = {
            trackId,
            score: form.score || 0,
            comment: form.comment || '',
            voiceCommentId,
        };

        // Если критериальная система, добавляем оценки по критериям
        if (rule?.votingSystem === 'CRITERIA_BASED' && form.criteriaScores) {
            payload.criteriaScores = form.criteriaScores;
        }

        // Если система +/0/-, преобразуем оценку
        if (rule?.votingSystem === 'PLUS_MINUS_ZERO') {
            // 0 = -1 (минус), 1-4 = 0 (ноль), 5-10 = 1 (плюс)
            if (form.score <= 0) payload.score = -1;
            else if (form.score <= 4) payload.score = 0;
            else payload.score = 1;
        }

        // Если CUSTOM_XYZ, проверяем валидность оценки
        if (rule?.votingSystem === 'CUSTOM_XYZ' && rule.validScores) {
            if (!rule.validScores.includes(form.score)) {
                setError(`Неверная оценка. Допустимые значения: ${rule.validScores.join(', ')}`);
                return;
            }
        }

        setLoading(prev => ({ ...prev, [roundId]: true }));
        setError(null);

        try {
            await votesApi.vote(battleId, roundId, payload);
            setUserVotes(prev => ({ ...prev, [`${roundId}-${trackId}`]: true }));
            await fetchResults(roundId);
            // Очищаем форму
            setVoteForms(prev => ({ ...prev, [roundId]: null }));
            setVoiceCommentFiles(prev => ({ ...prev, [roundId]: null }));
        } catch (err) {
            setError(err.response?.data?.message || 'Не удалось отправить голос');
            console.error('[BattleVoting] Failed to vote:', err.message);
        } finally {
            setLoading(prev => ({ ...prev, [roundId]: false }));
        }
    };

    const handleUpdateVote = async (roundId, trackId) => {
        const form = voteForms[roundId] || {};
        const rule = votingRules[roundId];
        const voiceFile = voiceCommentFiles[roundId];

        let voiceCommentId = form.voiceCommentId;

        // Если загружен новый файл, загружаем его сначала
        if (voiceFile) {
            setUploadingVoice(prev => ({ ...prev, [roundId]: true }));
            try {
                const uploadResult = await votesApi.uploadVoiceComment(voiceFile);
                voiceCommentId = uploadResult.id;
            } catch (err) {
                setError(err.message || 'Не удалось загрузить голосовой комментарий');
                console.error('[BattleVoting] Failed to upload voice comment:', err);
                setUploadingVoice(prev => ({ ...prev, [roundId]: false }));
                return;
            }
            setUploadingVoice(prev => ({ ...prev, [roundId]: false }));
        }

        const payload = {
            trackId,
            score: form.score || 0,
            comment: form.comment || '',
            voiceCommentId,
        };

        // Если критериальная система, добавляем оценки по критериям
        if (rule?.votingSystem === 'CRITERIA_BASED' && form.criteriaScores) {
            payload.criteriaScores = form.criteriaScores;
        }

        // Если система +/0/-, преобразуем оценку
        if (rule?.votingSystem === 'PLUS_MINUS_ZERO') {
            if (form.score <= 0) payload.score = -1;
            else if (form.score <= 4) payload.score = 0;
            else payload.score = 1;
        }

        // Если CUSTOM_XYZ, проверяем валидность оценки
        if (rule?.votingSystem === 'CUSTOM_XYZ' && rule.validScores) {
            if (!rule.validScores.includes(form.score)) {
                setError(`Неверная оценка. Допустимые значения: ${rule.validScores.join(', ')}`);
                return;
            }
        }

        setLoading(prev => ({ ...prev, [roundId]: true }));
        setError(null);

        try {
            await votesApi.updateVote(battleId, roundId, payload);
            await fetchResults(roundId);
        } catch (err) {
            setError(err.response?.data?.message || 'Не удалось обновить голос');
            console.error('[BattleVoting] Failed to update vote:', err.message);
        } finally {
            setLoading(prev => ({ ...prev, [roundId]: false }));
        }
    };

    const handleDeleteVote = async (roundId, trackId) => {
        if (!window.confirm('Вы уверены, что хотите удалить свой голос?')) return;

        setLoading(prev => ({ ...prev, [roundId]: true }));
        try {
            await votesApi.deleteVote(battleId, roundId, trackId);
            setUserVotes(prev => ({ ...prev, [`${roundId}-${trackId}`]: false }));
            await fetchResults(roundId);
        } catch (err) {
            setError('Не удалось удалить голос');
            console.error('[BattleVoting] Failed to delete vote:', err.message);
        } finally {
            setLoading(prev => ({ ...prev, [roundId]: false }));
        }
    };

    const canVote = (round) => {
        if (!currentUser) return false;
        if (round.status !== 'RUNNING' && round.status !== 'FINISHED') return false;
        const session = sessions[round.id];
        return session && session.status === SESSION_STATUS.OPEN;
    };

    const canViewResults = (round) => {
        const session = sessions[round.id];
        return session && session.status === SESSION_STATUS.CLOSED;
    };

    // Инициализация формы голосования на основе правила
    const getVoteForm = (roundId, trackId) => {
        const existingForm = voteForms[`${roundId}-${trackId}`];
        if (existingForm) return existingForm;

        const rule = votingRules[roundId];
        const defaultForm = { score: 5, comment: '' };

        if (rule?.votingSystem === 'CRITERIA_BASED' && rule.criteriaDefinitions) {
            // Инициализируем оценки по критериям (средние значения)
            defaultForm.criteriaScores = rule.criteriaDefinitions.map(def => ({
                criterionName: def.name,
                score: Math.floor((def.minScore + def.maxScore) / 2)
            }));
            // Вычисляем общую сумму
            defaultForm.score = defaultForm.criteriaScores.reduce((sum, cs) => sum + cs.score, 0);
        }

        return defaultForm;
    };

    // Получение отображения системы голосования
    const getVotingSystemLabel = (rule) => {
        if (!rule) return 'Голосование';
        if (rule.votingSystem === 'CRITERIA_BASED') {
            return rule.votingSystemDescription || `Критерии (макс. ${rule.maxScore})`;
        }
        if (rule.votingSystem === 'PLUS_MINUS_ZERO') {
            return '+ / 0 / -';
        }
        if (rule.votingSystem === 'NUMERIC_10') {
            return 'Оценка 0-10';
        }
        return 'Голосование';
    };

    if (!rounds || rounds.length === 0) {
        return (
            <div className="battle-voting">
                <HrnbCard title="Голосование" className="battle-voting-card">
                    <div className="battle-voting-empty">
                        <span className="battle-voting-empty-icon">🎯</span>
                        <p>Раунды ещё не созданы</p>
                    </div>
                </HrnbCard>
            </div>
        );
    }

    return (
        <div className="battle-voting">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)} className="mb-3">
                    {error}
                </HrnbAlert>
            )}

            <div className="battle-voting-rounds">
                {rounds.map((round) => {
                    const session = sessions[round.id];
                    const result = results[round.id];
                    const rule = votingRules[round.id];
                    const trackId = round.trackId;
                    const hasVoted = trackId ? userVotes[`${round.id}-${trackId}`] : false;
                    const isLoading = loading[round.id];
                    const form = getVoteForm(round.id, trackId);
                    const votingSystemLabel = getVotingSystemLabel(rule);

                    return (
                        <HrnbCard
                            key={round.id}
                            title={`Раунд ${round.number}: ${votingSystemLabel}`}
                            className="battle-voting-round-card"
                        >
                            <div className="battle-voting-round-header">
                                <div className="battle-voting-round-meta">
                                    <span className="battle-voting-round-status">
                                        {ROUND_STATUS[round.status] || round.status}
                                    </span>
                                    <span className="battle-voting-round-time">
                                        {formatDate(round.startTime)} — {formatDate(round.endTime)}
                                    </span>
                                </div>
                                {session && (
                                    <span className={`battle-voting-session-badge ${session.status?.toLowerCase()}`}>
                                        {session.status === SESSION_STATUS.OPEN ? '🗳️ Голосование открыто' : '🔒 Голосование закрыто'}
                                    </span>
                                )}
                            </div>

                            {/* Информация о системе голосования */}
                            {rule && rule.votingSystem === 'CRITERIA_BASED' && (
                                <div className="battle-voting-criteria-info">
                                    <h5 className="battle-voting-criteria-title">Критерии оценки:</h5>
                                    <div className="battle-voting-criteria-list">
                                        {rule.criteriaDefinitions?.map((criteria, idx) => (
                                            <div key={idx} className="battle-voting-criteria-item">
                                                <span className="battle-voting-criteria-name">{criteria.name}</span>
                                                <span className="battle-voting-criteria-range">
                                                    ({criteria.minScore}-{criteria.maxScore} баллов)
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="battle-voting-max-score">
                                        Максимальный балл: <strong>{rule.maxScore}</strong>
                                    </div>
                                </div>
                            )}

                            {/* Форма голосования */}
                            {canVote(round) && (
                                <div className="battle-voting-form">
                                    {!currentUser ? (
                                        <div className="battle-voting-auth-required">
                                            Войдите, чтобы проголосовать
                                        </div>
                                    ) : (
                                        <>
                                            {/* Критериальная система */}
                                            {rule?.votingSystem === 'CRITERIA_BASED' ? (
                                                <div className="battle-voting-criteria-form">
                                                    {rule.criteriaDefinitions?.map((criteria, idx) => {
                                                        const criteriaScore = form.criteriaScores?.find(
                                                            cs => cs.criterionName === criteria.name
                                                        ) || { score: Math.floor((criteria.minScore + criteria.maxScore) / 2) };

                                                        return (
                                                            <div key={idx} className="battle-voting-criteria-score">
                                                                <label className="battle-voting-label">
                                                                    {criteria.name}: <strong>{criteriaScore.score}</strong> / {criteria.maxScore}
                                                                </label>
                                                                <HrnbSlider
                                                                    min={criteria.minScore}
                                                                    max={criteria.maxScore}
                                                                    step={1}
                                                                    value={criteriaScore.score}
                                                                    onChange={(val) => handleCriteriaScoreChange(round.id, criteria.name, val)}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="battle-voting-total-score">
                                                        <strong>Общий балл: {form.score}</strong> / {rule.maxScore}
                                                    </div>
                                                </div>
                                            ) : (
                                                /* Числовая система или +/0/- */
                                                <div className="battle-voting-score-section">
                                                    <label className="battle-voting-label">
                                                        {rule?.votingSystem === 'PLUS_MINUS_ZERO'
                                                            ? 'Ваша оценка: ' + (form.score <= 0 ? '➖ Минус' : form.score <= 4 ? '⭕ Ноль' : '➕ Плюс')
                                                            : `Ваша оценка: ${form.score} / ${rule?.maxScore || 10}`
                                                        }
                                                    </label>
                                                    <HrnbSlider
                                                        min={rule?.votingSystem === 'PLUS_MINUS_ZERO' ? -1 : 0}
                                                        max={rule?.maxScore || 10}
                                                        step={1}
                                                        value={form.score ?? 5}
                                                        onChange={(val) => handleVoteChange(round.id, 'score', val)}
                                                    />
                                                    {rule?.votingSystem === 'PLUS_MINUS_ZERO' && (
                                                        <div className="battle-voting-score-hints">
                                                            <span>➖ Минус (-1)</span>
                                                            <span>⭕ Ноль (0)</span>
                                                            <span>➕ Плюс (+1)</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="battle-voting-comment-section">
                                                <label className="battle-voting-label">Комментарий (опционально)</label>
                                                <HrnbTextArea
                                                    value={form.comment || ''}
                                                    onChange={(e) => handleVoteChange(round.id, 'comment', e.target.value)}
                                                    placeholder="Ваш комментарий к треку..."
                                                    rows={3}
                                                />
                                            </div>

                                            {/* Голосовой комментарий */}
                                            {rule?.allowVoiceComment && (
                                                <div className="battle-voting-voice-comment-section">
                                                    <label className="battle-voting-label">Голосовой комментарий (опционально)</label>
                                                    <HrnbFileUpload
                                                        accept="audio/*"
                                                        onChange={(file) => handleVoiceCommentUpload(round.id, file)}
                                                        label={voiceCommentFiles[round.id] ? voiceCommentFiles[round.id].name : 'Загрузить аудиофайл'}
                                                        disabled={uploadingVoice[round.id]}
                                                    />
                                                    {uploadingVoice[round.id] && (
                                                        <div className="battle-voting-uploading">
                                                            <HrnbLoader size="sm" /> Загрузка аудио...
                                                        </div>
                                                    )}
                                                    {form.voiceComment && !voiceCommentFiles[round.id] && (
                                                        <div className="battle-voting-existing-voice">
                                                            <small>📎 Прикреплён предыдущий голосовой комментарий</small>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="battle-voting-actions">
                                                {hasVoted ? (
                                                    <>
                                                        <HrnbButton
                                                            variant="primary"
                                                            onClick={() => handleUpdateVote(round.id, trackId)}
                                                            disabled={isLoading}
                                                        >
                                                            {isLoading ? <HrnbLoader size="sm" /> : 'Обновить голос'}
                                                        </HrnbButton>
                                                        <HrnbButton
                                                            variant="outline-danger"
                                                            onClick={() => handleDeleteVote(round.id, trackId)}
                                                            disabled={isLoading}
                                                        >
                                                            Удалить голос
                                                        </HrnbButton>
                                                    </>
                                                ) : (
                                                    <HrnbButton
                                                        variant="primary"
                                                        onClick={() => handleSubmitVote(round.id, trackId)}
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading ? <HrnbLoader size="sm" /> : 'Проголосовать'}
                                                    </HrnbButton>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Результаты голосования */}
                            {canViewResults(round) && result && (
                                <div className="battle-voting-results">
                                    <h4 className="battle-voting-results-title">🏆 Результаты</h4>
                                    <div className="battle-voting-results-grid">
                                        <div className="battle-voting-result-item">
                                            <span className="battle-voting-result-value">{result.averageScore?.toFixed(1) || '—'}</span>
                                            <span className="battle-voting-result-label">Средняя оценка</span>
                                        </div>
                                        <div className="battle-voting-result-item">
                                            <span className="battle-voting-result-value">{result.totalScore || '—'}</span>
                                            <span className="battle-voting-result-label">Общий балл</span>
                                        </div>
                                        <div className="battle-voting-result-item">
                                            <span className="battle-voting-result-value">{result.votesCount || 0}</span>
                                            <span className="battle-voting-result-label">Голосов</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Если сессия ещё не открыта */}
                            {!session && round.status === 'PENDING' && (
                                <div className="battle-voting-pending">
                                    ⏳ Голосование начнётся после начала раунда
                                </div>
                            )}

                            {/* Если сессия закрыта, но результатов нет */}
                            {session?.status === SESSION_STATUS.CLOSED && !result && (
                                <div className="battle-voting-pending">
                                    Результаты обрабатываются...
                                </div>
                            )}
                        </HrnbCard>
                    );
                })}
            </div>
        </div>
    );
};

export default BattleVoting;
